import React, {useEffect} from 'react';
import {X, Trash2} from 'lucide-react';
import {Link, useSearchParams} from "react-router-dom";
import {Device} from "../types/device";
import {useComparison} from "../api/client";
import ComparisonSpecifications from "../components/ComparePage/ComparisonSpecifications";
import {useCompareStore} from "../stores/useCompateStore";
import ComparisonSearchBar from "../components/ComparePage/ComparisonSearchBar";

export default function ComparisonPage() {
    // Mock data based on your screenshot

    const { ids, setIds, removeFromCompare, clearCompare } = useCompareStore();
    const [searchParams, setSearchParams] = useSearchParams();

    // 1. URL Sync Logic
    useEffect(() => {
        const urlParam = searchParams.get('ids');
        if (urlParam) {
            const parsedIds = urlParam.split(',').map(Number).filter(n => !!n);

            // If URL has data but store is empty (fresh browser), update store
            if (parsedIds.length > 0 && ids.length === 0) {
                setIds(parsedIds);
            }
        }
    }, []); // Run on mount only

    useEffect(() => {
        if (ids.length > 0) {
            setSearchParams({ ids: ids.join(',') }, { replace: true });
        } else {
            setSearchParams({}, { replace: true });
        }
    }, [ids, setSearchParams]);

    // 2. Data Fetching
    const {data: devices, isLoading} = useComparison(ids);

    return (
        <div className="min-h-screen bg-white text-slate-900 p-8 font-sans">
            {!isLoading ? (<div className="max-w-7xl mx-auto">

                    {/* --- HEADER SECTION --- */}
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h1 className="text-3xl font-bold mb-6">Comparison of {devices?.length} mobile phones</h1>
                            <ComparisonSearchBar/>
                        </div>

                        <button className="flex flex-col items-center gap-1 group">
                            <div
                                className="p-3 rounded-full bg-slate-100 group-hover:bg-red-50 group-hover:text-red-600 transition-all"
                                onClick={clearCompare}>
                                <Trash2 size={24}/>
                            </div>
                            <span
                                className="text-sm font-medium text-slate-500 group-hover:text-red-600">Clear table</span>
                        </button>
                    </div>

                    {/* --- DEVICE CARDS ROW --- */}
                    <div className="grid grid-cols-12 gap-6 mb-12">
                        <div className="col-span-3"></div>

                        <div
                            className="col-span-9 grid gap-6"
                            style={{gridTemplateColumns: `repeat(${devices?.length || 1}, 240px)`}}
                        >
                            {devices?.map((device: Device) => (
                                <div key={device.id} className="flex flex-col items-start">
                                    <div className="w-full max-w-[240px] relative">

                                        <button
                                            onClick={() => removeFromCompare(device.id)}
                                            className="absolute top-4 right-4 p-1.5 bg-white border border-slate-200 rounded-full z-20 hover:bg-red-50 hover:text-red-600 shadow-sm transition-colors"
                                        >
                                            <X size={14}/>
                                        </button>

                                        <div
                                            className="h-[300px] w-full bg-slate-50 border-2 border-slate-900 rounded-[40px] flex flex-col items-center p-6 text-center">
                                            <Link to={`/phones/${device.id}`}
                                                  className="block no-underline text-inherit">
                                                <div
                                                    className="flex-grow flex items-center justify-center w-full min-h-0">
                                                    <img
                                                        src={device.image_url ?? ""}
                                                        alt={device.model}
                                                        className="max-h-full w-auto drop-shadow-xl object-contain"
                                                    />
                                                </div>

                                                <div className="mt-4 h-[40px] flex items-center justify-center">
                                                    <h3 className="font-bold text-sm md:text-base leading-tight">
                                                        {device.brand}<br/>{device.model}
                                                    </h3>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* --- PRICES SECTION --- */}
                    <div className="grid grid-cols-12 gap-6 mb-16 items-start">
                        <div className="col-span-3 pt-4">
                            <div className="inline-block border-b-2 border-slate-900 pb-1">
                                <h2 className="text-2xl font-bold">Prices</h2>
                            </div>
                            <p className="mt-4 font-semibold text-sm">Top Sellers</p>
                        </div>


                        <div
                            className="col-span-9 grid gap-6"
                            style={{gridTemplateColumns: `repeat(${devices?.length || 1}, 240px)`}}
                        >
                            {devices?.map((device: Device, idx: number) => (
                                <div key={idx} className="flex justify-start">
                                    <div
                                        className="w-full max-w-[240px] min-h-[350px] bg-white border border-slate-100 shadow-xl shadow-slate-200/50 rounded-[32px] p-6 flex flex-col">
                                        <p className="text-red-500 font-bold mb-4 italic uppercase text-xs text-center">from
                                            €999</p>

                                        <div className="space-y-3 mb-6">
                                            {[1, 2, 3].map((s) => (
                                                <div key={s}
                                                     className="flex items-center justify-between px-2 py-1 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors group">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="w-6 h-6 bg-purple-900 rounded-full flex items-center justify-center text-[10px] text-white font-bold">currys
                                                        </div>
                                                        <span
                                                            className="text-blue-600 underline text-sm font-medium decoration-blue-300">currys.ie</span>
                                                    </div>
                                                    <span className="text-red-500 font-bold text-sm">€999.99</span>
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            className="w-full py-3 px-4 bg-slate-50 rounded-2xl text-slate-700 font-bold text-sm hover:bg-slate-100 transition-colors">
                                            Compare among X shops
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* --- SPECS SECTION --- */}
                    <div className="relative">

                        <ComparisonSpecifications devices={devices ?? []}/>
                    </div>
                </div>
            ) : (<p>Loading</p>)}
        </div>
    );
};
